import {
  getSKUFromProductId,
  isSub,
  getPrintTypeFromProductId,
  isDecorationNotNeeded,
} from "selectors/products";
import moment from "moment";

const regExpr = /[^a-zA-Z0-9- ]/g;

const json2xmlPrintAreas = (
  order,
  products,
  orderLineItem,
  vatIncluded,
  iossNumber
) => {
  const isSam = order?.MasterUserEmail === "sam@bemerchful.com";
  const isBardown = order?.MasterUserEmail === "support@gobardown.com";
  let items = "";
  const salesValueInEuro = (
    (orderLineItem?.FulfillmentCost +
      (orderLineItem?.FulfillmentCost * 40) / 100) *
    1.18
  ).toFixed(2);
  const printAreas = orderLineItem?.printAreas;

  const availablePrintArtwork = orderLineItem?.aopVectorFile
    ? printAreas
    : printAreas?.filter((p) => p?.artwork);

  let sku = getSKUFromProductId(products, orderLineItem?.ProductID);

  if (isSam) {
    sku = orderLineItem?.MarketPlaceDetails?.sku;
  }

  const isDecorationOptional = isDecorationNotNeeded(
    products,
    orderLineItem?.ProductID
  );
  const printType =
    orderLineItem?.PrintType ||
    getPrintTypeFromProductId(products, orderLineItem?.ProductID);
  const isOrderLineSub = isSub(products, orderLineItem?.ProductID);
  let productName = orderLineItem?.Product;

  if (isSam) {
    productName = orderLineItem?.MarketPlaceDetails?.title;
  }

  const vat = vatIncluded
    ? `(VALUE = €${salesValueInEuro} / QTY = ${orderLineItem.OliQuantity}${
        iossNumber ? ` / IOSS = ${iossNumber}` : ""
      })`
    : "";

  const marketPlaceDetails = orderLineItem?.MarketPlaceDetails;
  const properties = marketPlaceDetails?.properties;
  const isPersonlised = properties?.find((p) => p?.name === "Personalized");
  const logoType = properties?.find((p) => p?.name === "Logo Type");

  const isCertificateinSku = sku.includes("Certificate");
  const isDuffelBagInSku = sku.includes("Duffel Bag");
  const isPatchesInSku = sku.includes("Patches");

  items += `<Item>
    <SKU>${sku.replace(regExpr, "")}</SKU>
    <Descrip>${
      isSam
        ? `[Stock - BeMerchful] `
        : orderLineItem?.stockProvider
        ? `[STOCK-${orderLineItem?.stockReferenceNumber}]`
        : ""
    }${
    orderLineItem.DtgPlus
      ? `[${printType} ${
          !!isPersonlised ? ` - PERSONALIZED : ${isPersonlised?.value}` : ""
        }/${orderLineItem?.dtgColorName?.SwatchCode}] ${vat} ${productName}`
      : orderLineItem.Plus
      ? `[TUFF PRINT TRANSFER] [${orderLineItem?.dtgColorName?.SwatchCode}] ${vat} ${productName}`
      : `[${printType} ${
          !!isPersonlised ? ` - PERSONALIZED : ${isPersonlised?.value}` : ""
        } ${
          printType === "ScreenPrint"
            ? ` - ${orderLineItem?.numberOfColors} Colors`
            : ""
        }/${orderLineItem?.dtgColorName?.SwatchCode}] ${vat} ${productName}`
  }</Descrip>
    <Color>${
      isSam
        ? orderLineItem?.MarketPlaceDetails?.variant_title
        : orderLineItem.Color
    }</Color>
    <Qty>${
      isSam
        ? orderLineItem?.MarketPlaceDetails?.quantity
        : orderLineItem.OliQuantity
    }</Qty>
    <Size>${
      isSam
        ? orderLineItem?.MarketPlaceDetails?.variant_title
        : orderLineItem.Size
    }</Size>
    <Type>${printType}</Type>
    <Style>${sku.split("-")[0]?.replace(regExpr, "")}</Style>
    <ProcessMode>Piece</ProcessMode>`;

  if (isDecorationOptional) {
    for (let j = 0; j < printAreas?.length; j++) {
      const row = printAreas[j];
      const { printAreaMarketingImage, printAreaName, productArtFile } = row;

      items += `<Design>
        <Location>${printAreaName?.toUpperCase()}</Location>
        <Thumb>${printAreaMarketingImage}</Thumb>
        <SourceFile>${
          !!productArtFile
            ? productArtFile
            : isOrderLineSub
            ? printAreaMarketingImage.replace(".jpg", ".png")
            : printAreaMarketingImage.replace(".jpg", ".png")
        }</SourceFile>
        <Width>0</Width>
        <Height>0</Height>
        <LOffset>0</LOffset>
        <TOffset>0</TOffset>
        <WhiteUnderbaseRequired>false</WhiteUnderbaseRequired>
      </Design>`;
    }
  }

  for (let j = 0; j < availablePrintArtwork?.length; j++) {
    const row = availablePrintArtwork[j];

    let { designerSettings, coordinates, artwork, MockupSrc, printAreaName } =
      row;

    const isBardownLeftSleeveSelected =
      order?.MasterUserEmail === "support@gobardown.com" &&
      printAreaName === "Left Sleeve" &&
      artwork;

    if (!!isBardownLeftSleeveSelected) {
      MockupSrc =
        "https://www.gobardown.com/cdn/shop/products/Classic-Hoodie-Black.png?v=1682711304";
    }

    const { multiplier } = designerSettings || {};
    const { width, height, x = 0, y = 0 } = coordinates || {};

    const artWidth = parseFloat(width * multiplier);
    const artHeight = parseFloat(height * multiplier);
    const canvasCenterPoint = parseFloat(designerSettings.width) / 2;
    const newX = parseFloat(x);
    const imageCenter = parseFloat(width) / 2;

    let xPos;

    if (newX > canvasCenterPoint) {
      xPos = canvasCenterPoint - parseFloat(width) + imageCenter;
    } else if (newX < canvasCenterPoint) {
      const newValue = newX + imageCenter + 1;
      xPos = newValue === canvasCenterPoint ? 0 : newValue - canvasCenterPoint;
      xPos = xPos === 1 ? 0 : xPos;
    }

    var yPos = parseInt(y);

    let artworkSplit = artwork?.split("/upload/");
    let newArtwork = "";
    if (artworkSplit) {
      newArtwork = `${artworkSplit[0]}/upload/x_${Math.round(x)},y_${Math.round(
        y
      )}/${artworkSplit[1]}?${moment().unix()}_front`;
    }

    if (isSam) {
      MockupSrc = orderLineItem?.MockupSrc?.replace(".ai", ".png");
      if (
        MockupSrc ===
        "https://upload.wikimedia.org/wikipedia/commons/thumb/a/ac/No_image_available.svg/2048px-No_image_available.svg.png"
      ) {
        MockupSrc = order?.OrderLines[0]?.OrderLineItems[0]?.MockupSrc?.replace(
          ".pdf",
          ".png"
        ).replace(".ai", ".png");
      }
    }

    items += `
      ${
        orderLineItem?.aopVectorFile
          ? `
          <Design>
            <Location>FRONT</Location>
            <Thumb>${
              isSam ? MockupSrc : orderLineItem.MarketingImagePath
            }</Thumb>
            <SourceFile>${orderLineItem.MarketingImagePath}</SourceFile>
            <Width>0</Width>
            <Height>0</Height>
            <LOffset>0</LOffset>
            <TOffset>0</TOffset>
            <WhiteUnderbaseRequired>false</WhiteUnderbaseRequired>
          </Design>`
          : `<Design>
          <Location>${printAreaName?.toUpperCase()}</Location>
          <Thumb>${MockupSrc}</Thumb>
          <SourceFile>${
            isOrderLineSub
              ? newArtwork.replace(".jpg", ".png")
              : newArtwork.replace(".jpg", ".png")
          }</SourceFile>
          <Width>${Math.round(artWidth || 0)}</Width>
          <Height>${Math.round(artHeight || 0)}</Height>
          <LOffset>${Math.round(xPos * multiplier)}</LOffset>
          <TOffset>${Math.round(yPos * multiplier)}</TOffset>
          <WhiteUnderbaseRequired>false</WhiteUnderbaseRequired>
        </Design>`
      }`;

    if (!!isPersonlised && j === 0) {
      items += `
          <Design>
            <Location>RIGHT CHEST</Location>
            <Thumb>${MockupSrc}</Thumb>
            <SourceFile>${isPersonlised?.value}</SourceFile>
            <Width>1022</Width>
            <Height>511</Height>
            <LOffset>0</LOffset>
            <TOffset>0</TOffset>
            <WhiteUnderbaseRequired>false</WhiteUnderbaseRequired>
          </Design>`;
    }

    if (
      !!logoType &&
      !isCertificateinSku &&
      !isPatchesInSku &&
      !isDuffelBagInSku
    ) {
      items += `
          <Design>
            <Location>LEFT CHEST</Location>
            <Thumb>${
              isSam
                ? MockupSrc
                : logoType.value === "English"
                ? "https://images.shirtly.com/image/upload/v1665760618/BOTApp/sam%40bemerchful.com/ArtFiles/Ryder_Ever_Better_EN_2C_d8emdm.png"
                : "https://images.shirtly.com/image/upload/v1665760746/BOTApp/sam%40bemerchful.com/ArtFiles/Ryder_Ever_Better_FR_white_znwvqo.png"
            }</Thumb>
            <SourceFile>${
              logoType.value === "English"
                ? "https://images.shirtly.com/image/upload/v1665760618/BOTApp/sam%40bemerchful.com/ArtFiles/Ryder_Ever_Better_EN_2C_d8emdm.png"
                : "https://images.shirtly.com/image/upload/v1665760746/BOTApp/sam%40bemerchful.com/ArtFiles/Ryder_Ever_Better_FR_white_znwvqo.png"
            }</SourceFile>
            <Width>1022</Width>
            <Height>511</Height>
            <LOffset>0</LOffset>
            <TOffset>0</TOffset>
            <WhiteUnderbaseRequired>false</WhiteUnderbaseRequired>
          </Design>`;
    }

    if (isBardown) {
      items += `
          <Design>
            <Location>NECK LABEL</Location>
            <Thumb>https://cdn.shopify.com/s/files/1/0102/0513/4929/files/Bar_Down_Hockey_eb532c06-7061-4d59-99f6-12c31a4f2151_1200x1200.png?v=1622846778</Thumb>
            <SourceFile>https://cdn.shopify.com/s/files/1/0102/0513/4929/files/Bar_Down_Hockey_eb532c06-7061-4d59-99f6-12c31a4f2151_1200x1200.png?v=1622846778</SourceFile>
            <Width>0</Width>
            <Height>0</Height>
            <LOffset>0</LOffset>
            <TOffset>0</TOffset>
            <WhiteUnderbaseRequired>false</WhiteUnderbaseRequired>
          </Design>`;
    }
  }

  items += "</Item>";

  return items;
};

export { json2xmlPrintAreas };
